import React, { useEffect, useMemo, useState } from 'react';
import useComponentVisible from '../UI/ComponentVisible';
import DeleteNotificationModal from './DeleteNotificationModal';
import dotsImgUrl from '../../assets/three-dots-vertical.svg';
import NotificationModal from './NotificationModal';
import { useUserContext } from '../../contexts/UserContext';
import { Search, Plus, Pencil, Trash2, Bell } from 'lucide-react';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { AgTableCard } from '../UI';
import { getNotification } from '../../utils/APIs';
import { useContentfulContext } from '../../contexts/ContentfulContext';

const ManageNotifications = () => {
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [loading, setLoading] = useState(false);
    const [selectedAction, setSelectedAction] = useState('');
    const [notifications, setNotifications] = useState([]);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const { userData } = useUserContext();
    const [gridApi, setGridApi] = useState(null);
    const { content } = useContentfulContext();

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const res = await getNotification(userData?.token);
            if (res.status) {
                setNotifications(res.data);
            }
        } catch (error) {
            console.error("Error fetching notifications:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, []);

    const colDefs = [
        {
            headerName: content.ADMIN_NOTIFICATIONS.table_headers.title,
            field: "title",
            sortable: true,
            filter: true,
            cellRenderer: ({ data }) => (
                <div className="flex items-center gap-2 h-full">
                    <Bell size={14} className="text-stride-muted shrink-0" />
                    <button
                        className="font-medium text-stride-primary hover:text-stride-primary-hover hover:underline transition-colors text-left truncate"
                        onClick={() => {
                            setSelectedNotification(data);
                            setSelectedAction("edit");
                        }}
                    >
                        {data?.title}
                    </button>
                </div>
            ),
            suppressMovable: true,
            minWidth: 200,
        },
        {
            headerName: content.ADMIN_NOTIFICATIONS.table_headers.type,
            field: "type",
            sortable: true,
            filter: true,
            suppressMovable: true,
            cellRenderer: ({ data }) => (
                <div className="flex items-center h-full">
                    <span className="stride-badge-pill stride-badge-pill-neutral uppercase text-[11px] font-mono">
                        {data?.type || "Standard"}
                    </span>
                </div>
            ),
            minWidth: 120,
        },
        {
            headerName: content.ADMIN_NOTIFICATIONS.table_headers.description,
            field: "description",
            sortable: true,
            suppressMovable: true,
            cellRenderer: ({ data }) => (
                <span className="text-xs text-stride-body truncate" title={data?.description}>
                    {data?.description || "—"}
                </span>
            ),
            minWidth: 260,
        },
        {
            headerName: content.ADMIN_NOTIFICATIONS.table_headers.category,
            field: "category",
            sortable: true,
            suppressMovable: true,
            cellRenderer: ({ data }) => (
                <span className="text-xs text-stride-muted capitalize">
                    {data?.category || "—"}
                </span>
            ),
            minWidth: 130,
        },
        {
            headerName: content.ADMIN_NOTIFICATIONS.table_headers.deep_link,
            field: "deep_link",
            sortable: true,
            suppressMovable: true,
            cellRenderer: ({ data }) => (
                <span className="text-xs font-mono text-stride-muted truncate" title={data?.deep_link}>
                    {data?.deep_link || "—"}
                </span>
            ),
            minWidth: 180,
        },
        {
            headerName: "Actions",
            field: "actions",
            cellRenderer: ({ data }) => (
                <div className="flex items-center justify-end gap-1.5 h-full">
                    {/* Direct tactile buttons */}
                    <button
                        type="button"
                        className="stride-table-btn stride-table-btn-edit"
                        title="Edit Notification"
                        onClick={() => {
                            setSelectedNotification(data);
                            setSelectedAction("edit");
                        }}
                    >
                        <Pencil size={13} />
                    </button>
                    <button
                        type="button"
                        className="stride-table-btn stride-table-btn-danger"
                        title="Delete Notification"
                        onClick={() => {
                            setSelectedNotification(data);
                            setSelectedAction("delete");
                        }}
                    >
                        <Trash2 size={13} />
                    </button>

                    {/* Backwards compatibility 3-dots */}
                    <button
                        type="button"
                        aria-label="Actions"
                        className="actions stride-table-btn stride-table-btn-secondary"
                        onClick={(e) => {
                            setSelectedNotification(data);
                            setIsComponentVisible(!isComponentVisible);
                            const buttonRect = e.currentTarget.getBoundingClientRect();
                            setMenuPosition({
                                top: buttonRect.bottom + window.scrollY,
                                left: buttonRect.left + window.scrollX,
                            });
                        }}
                    >
                        <img src={dotsImgUrl} alt="Actions" className="w-3.5 h-3.5 opacity-70" />
                    </button>
                </div>
            ),
            width: 140,
            minWidth: 140,
            sortable: false,
        },
    ];

    const autoSizeStrategy = useMemo(() => ({
        type: "fitGridWidth",
        defaultMinWidth: 120,
        columnLimits: [
            { colId: "title", minWidth: 200 },
            { colId: "type", minWidth: 120 },
            { colId: "description", minWidth: 260 },
            { colId: "category", minWidth: 130 },
            { colId: "deep_link", minWidth: 180 },
            { colId: "actions", minWidth: 140 }
        ],
    }), []);

    const onFilterTextBoxChanged = (event) => {
        const filterValue = event.target.value.toLowerCase();
        if (gridApi) {
            gridApi.setFilterModel({
                title: { type: 'contains', filter: filterValue }
            });
        }
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    return (
        <div className="stride-page-shell">
                <main className="w-full space-y-6">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wider text-stride-muted mb-1">
                                System Administration
                            </p>
                            <h1 className="text-2xl font-semibold tracking-tight text-stride-heading">
                                {content.ADMIN_NOTIFICATIONS.title || "Manage Notifications"}
                            </h1>
                            <p className="text-sm text-stride-body mt-0.5">
                                Configure global system announcements, in-app notifications, and deep links.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 self-start sm:self-auto">
                            <Button
                                variant="primary"
                                icon={<Plus size={16} />}
                                onClick={() => {
                                    setSelectedAction("create");
                                    setSelectedNotification(null);
                                }}
                            >
                                {content.ADMIN_NOTIFICATIONS.cta_text.create_notification || "Create Notification"}
                            </Button>
                        </div>
                    </div>

                    {/* Table Card */}
                    <AgTableCard
                        header={
                            <div className="w-full sm:w-80">
                                <Input
                                    type="text"
                                    placeholder="Search notifications by title..."
                                    prefixIcon={<Search size={16} />}
                                    onChange={onFilterTextBoxChanged}
                                    clearable
                                />
                            </div>
                        }
                        tableId="manage-notifications-table"
                        loading={loading}
                        rowData={notifications}
                        columnDefs={colDefs}
                        autoSizeStrategy={autoSizeStrategy}
                        onGridReady={onGridReady}
                    >
                        {/* Dropdown Menu for backwards compatibility & 3-dots */}
                        {isComponentVisible && selectedNotification && (
                            <div
                                ref={ref}
                                className="absolute z-10 w-48 rounded-lg bg-white shadow-lg border border-stride-border py-1 ring-1 ring-black ring-opacity-5 animate-in fade-in zoom-in-95 duration-100"
                                style={{ top: menuPosition.top, left: Math.max(16, menuPosition.left - 180) }}
                            >
                                <a
                                    href="#edit"
                                    className="block px-4 py-2 text-sm text-stride-heading hover:bg-stride-hover transition-colors"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        setSelectedAction('edit');
                                    }}
                                >
                                    {content.ADMIN_NOTIFICATIONS.more_info.edit || "Edit"}
                                </a>
                                <a
                                    href="#delete"
                                    className="block px-4 py-2 text-sm text-stride-danger hover:bg-stride-danger/10 transition-colors"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        setSelectedAction('delete');
                                    }}
                                >
                                    {content.ADMIN_NOTIFICATIONS.more_info.delete || "Delete"}
                                </a>
                            </div>
                        )}
                    </AgTableCard>
                </main>

                {selectedAction === "create" && (
                    <NotificationModal setSelectedAction={setSelectedAction} getNotifications={fetchNotifications} />
                )}
                {selectedAction === "edit" && (
                    <NotificationModal setSelectedAction={setSelectedAction} selectedNotification={selectedNotification} getNotifications={fetchNotifications} />
                )}
                {selectedAction === "delete" && (
                    <DeleteNotificationModal
                        setSelectedAction={setSelectedAction}
                        selectedNotification={selectedNotification}
                        setSelectedNotification={setSelectedNotification}
                        getNotifications={fetchNotifications}
                    />
                )}
            </div>
    );
};

export default ManageNotifications;
